/**
 * `useOmegaState` — 2-second polling hook for the OMEGA L1 page.
 *
 * Per `docs/OMEGA-DASHBOARD-SPEC.md` §7.1, top-level state polls
 * every 2s. Both hooks fetch live data from the engine log via
 * `GET /api/v1/omega/state` and `GET /api/v1/omega/brains`. On
 * failure React Query enters error state and retries on the next
 * interval — the dashboard should handle the error boundary.
 */

import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/api/http';

import type {
  BrainSummary,
  LaunchPlansResponse,
  MemoryEntry,
  MemorySummary,
  OmegaProposalsResponse,
  OmegaState,
  PendingDecisionsResponse,
  RecentThreatsResponse,
} from '../data/types';

// ─── Query keys ────────────────────────────────────────────────────

export const omegaQueryKeys = {
  all: ['omega'] as const,
  state: () => [...omegaQueryKeys.all, 'state'] as const,
  brains: () => [...omegaQueryKeys.all, 'brains'] as const,
  proposals: () => [...omegaQueryKeys.all, 'proposals'] as const,
  decisions: () => [...omegaQueryKeys.all, 'decisions'] as const,
  threats: () => [...omegaQueryKeys.all, 'threats'] as const,
  memory: () => [...omegaQueryKeys.all, 'memory'] as const,
  launchPlans: (domain?: string, status?: string) =>
    [...omegaQueryKeys.all, 'launchplans', domain ?? '', status ?? ''] as const,
};

// ─── API client ────────────────────────────────────────────────────
//
// All OMEGA hooks share `apiFetch` from the central http client.
// That client always sends `Authorization: Bearer <jwt>` (falling
// back to the dev bearer when no session exists), which satisfies
// the `require_founder` middleware on /omega/* and /memory/* routes.
// Paths here omit the `/api/v1` prefix — `apiFetch` prepends it.

function fetchJson<T>(path: string): Promise<T> {
  // Strip /api/v1 prefix so apiFetch can add it correctly.
  const bare = path.startsWith('/api/v1') ? path.slice(7) : path;
  return apiFetch<T>(bare);
}

// ─── Offline fallback stub ─────────────────────────────────────────
//
// When the engine API is unreachable (dev, Playwright offline tests)
// `useOmegaState` would return `data: undefined` and trigger a crash
// in OmegaStatusSection at `state.alive`. This stub provides a safe
// default so the dashboard renders offline without throwing.

const OMEGA_STATE_STUB: OmegaState = {
  root_id: 'stub-offline',
  founder_pubkey: '',
  engine_pubkey: '',
  born_iso: new Date().toISOString(),
  consciousness_stance: 'UncertaintyLive',
  alive: true,
  ratified: false,
  envelope_published: false,
  factory_sparked: false,
  worker_total: 0,
  log_entries_total: 0,
  decisions_per_minute: 0,
  refusals_per_minute: 0,
  attestor: { minutes_since_anchor: 0, anchor_count: 0, divergence: false },
  kill_switch: { initiated: false, collected: 0, required: 3, last_drill_iso: null },
};

// ─── Public hooks ──────────────────────────────────────────────────

/** Top-level OMEGA state. Polls every 2s from the live engine log.
 *  Falls back to OMEGA_STATE_STUB when the endpoint is unreachable
 *  so OmegaStatusSection never receives undefined. */
export function useOmegaState() {
  return useQuery<OmegaState>({
    queryKey: omegaQueryKeys.state(),
    queryFn: () =>
      fetchJson<OmegaState>('/api/v1/omega/state').catch(() => OMEGA_STATE_STUB),
    refetchInterval: 2_000,
    staleTime: 1_500,
  });
}

/** Per-brain summaries for the L1 brain-status grid. Polls every 2s. */
export function useBrainSummaries() {
  return useQuery<BrainSummary[]>({
    queryKey: omegaQueryKeys.brains(),
    queryFn: () => fetchJson<BrainSummary[]>('/api/v1/omega/brains'),
    refetchInterval: 2_000,
    staleTime: 1_500,
  });
}

/** OMEGA Brain proposals — pending + resolved. Polls every 10s. */
export function useOmegaProposals() {
  return useQuery<OmegaProposalsResponse>({
    queryKey: omegaQueryKeys.proposals(),
    queryFn: () => fetchJson<OmegaProposalsResponse>('/api/v1/omega/proposals'),
    refetchInterval: 10_000,
    staleTime: 8_000,
  });
}

/** Defense Brain threat feed. Polls every 15s. */
export function useThreats() {
  return useQuery<RecentThreatsResponse>({
    queryKey: omegaQueryKeys.threats(),
    queryFn: () => fetchJson<RecentThreatsResponse>('/api/v1/defense/threats/recent'),
    refetchInterval: 15_000,
    staleTime: 12_000,
  });
}

/** OMEGA Memory summary — total, by_type, recent lessons. Polls every 30s. */
export function useMemorySummary() {
  return useQuery<MemorySummary>({
    queryKey: omegaQueryKeys.memory(),
    queryFn: () => fetchJson<MemorySummary>('/api/v1/memory/summary'),
    refetchInterval: 30_000,
    staleTime: 25_000,
  });
}

/** Launch Plans from the Market Discovery Brain. Polls every 60s. */
export function useLaunchPlans(domain?: string, status?: string) {
  const params = new URLSearchParams();
  if (domain) params.set('domain', domain);
  if (status) params.set('status', status);
  const qs = params.toString() ? `?${params.toString()}` : '';
  return useQuery<LaunchPlansResponse>({
    queryKey: omegaQueryKeys.launchPlans(domain, status),
    queryFn: () => fetchJson<LaunchPlansResponse>(`/api/v1/omega/launchplans${qs}`),
    refetchInterval: 60_000,
    staleTime: 55_000,
  });
}

/** Decisions awaiting founder action. Polls every 10s. */
export function useDecisions() {
  return useQuery<PendingDecisionsResponse>({
    queryKey: omegaQueryKeys.decisions(),
    queryFn: () =>
      fetchJson<PendingDecisionsResponse>('/api/v1/omega/decisions/pending'),
    refetchInterval: 10_000,
    staleTime: 8_000,
  });
}

/** Memory entries relevant to a specific plan (for drawer context section). */
export function usePlanMemoryContext(planId: string, domain: string, enabled: boolean) {
  const tags = `product:${planId},domain:${domain},market_discovery`;
  return useQuery<{ results: MemoryEntry[]; total: number }>({
    queryKey: [...omegaQueryKeys.all, 'memory', 'plan', planId] as const,
    queryFn: () =>
      fetchJson<{ results: MemoryEntry[]; total: number }>(
        `/api/v1/memory/search?tags=${encodeURIComponent(tags)}&limit=5`,
      ),
    enabled,
    staleTime: 60_000,
  });
}
